import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Users, UserPlus, BadgeDollarSign, Wallet } from 'lucide-react';
import { formatUsd } from '@/lib/clientsData';

const Stat = ({ label, value, hint, Icon, tone = 'default', testId }) => {
  const toneStyles = {
    default: 'text-foreground',
    primary: 'text-[hsl(var(--primary))]',
    success: 'text-[hsl(var(--success))]',
    info: 'text-[hsl(var(--info))]',
  };
  const accentBar = {
    default: 'bg-border',
    primary: 'bg-[hsl(var(--primary))]/70',
    success: 'bg-[hsl(var(--success))]/70',
    info: 'bg-[hsl(var(--info))]/70',
  };
  return (
    <Card
      className="relative overflow-hidden border-[hsl(var(--hairline))] bg-[hsl(var(--card))] hover:border-[hsl(var(--primary))]/30 transition-colors"
      data-testid={testId}
    >
      <span
        aria-hidden
        className={cn('metallic absolute left-0 top-0 h-full w-[2px]', accentBar[tone])}
      />
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] leading-snug sm:text-sm lg:text-xs 2xl:text-sm font-bold uppercase tracking-[0.12em] break-words text-[hsl(var(--primary))]">
              {label}
            </div>
            <div
              className={cn('mt-1.5 text-lg sm:text-xl font-semibold tracking-tight tabular-nums truncate', toneStyles[tone])}
              data-testid={testId ? `${testId}-value` : undefined}
            >
              {value}
            </div>
            {hint ? (
              <div className="mt-1 text-xs text-muted-foreground truncate">{hint}</div>
            ) : null}
          </div>
          {Icon ? (
            <div
              className={cn(
                'hidden sm:flex shrink-0 size-9 lg:size-8 2xl:size-9 rounded-md border border-border bg-[hsl(var(--muted))] items-center justify-center',
                toneStyles[tone]
              )}
            >
              <Icon size={18} strokeWidth={1.75} />
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export const ContactsStatsBar = ({ stats }) => {
  const { t } = useTranslation();
  const { clients = 0, leads = 0, pipelineValue = 0, lifetimeValue = 0 } = stats || {};
  return (
    <div
      className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
      data-testid="contacts-stats"
    >
      <Stat
        label={t('clientsLeads.stats.totalClients')}
        value={clients}
        Icon={Users}
        tone="primary"
        hint={t('clientsLeads.stats.clientsCaption')}
        testId="stat-total-clients"
      />
      <Stat
        label={t('clientsLeads.stats.totalLeads')}
        value={leads}
        Icon={UserPlus}
        tone="info"
        hint={t('clientsLeads.stats.leadsCaption')}
        testId="stat-total-leads"
      />
      <Stat
        label={t('clientsLeads.stats.pipelineValue')}
        value={formatUsd(pipelineValue)}
        Icon={BadgeDollarSign}
        tone="success"
        hint={t('clientsLeads.stats.pipelineCaption')}
        testId="stat-pipeline-value"
      />
      <Stat
        label={t('clientsLeads.stats.clientLtv')}
        value={formatUsd(lifetimeValue)}
        Icon={Wallet}
        tone="default"
        hint={t('clientsLeads.stats.ltvCaption')}
        testId="stat-lifetime-value"
      />
    </div>
  );
};

export default ContactsStatsBar;
